/**
 * The in-headset UI: three UI Extensions windows built from UIKitML.
 *
 * The desk build drives the atlas from a DOM panel, which cannot be reached
 * once the headset is on. These are the same controls as spatial windows, and
 * they are the only interface that matters on a Quest.
 *
 * The windows hold no state. Each one reads an `AtlasUiPort` and calls it, the
 * same way the DOM panel does, so both interfaces stay in step by construction
 * rather than by discipline. What to show is decided by the rules
 * (`rules/visibility.ts`, `rules/browse-nav.ts`); this file only paints and
 * forwards presses.
 *
 * Two constraints the markup imposes, both from the uikit panel model:
 *
 *  - **Children cannot be added after parse.** Every list is a fixed set of
 *    pre-allocated slots and unused ones are collapsed with `display: none`.
 *    That is why Browse pages rather than scrolls.
 *  - **A panel is found by id.** `panel.getElementById` is the whole binding
 *    surface, so the markup ids here and in `public/ui/*.uikitml` are a
 *    contract; changing one without the other fails silently and invisibly.
 */
import type { Entity, World } from "@iwsdk/core";
import { createSceneHost, createUIWindow, type IwsdkSceneHost } from "@realitycollective/iwsdk-uiextensions";
import type { PanelHandle, UixElement } from "@realitycollective/webxr-uiextensions";
import type { Catalog, Group } from "../model.js";
import { LAYER_PRESETS, type LayerPreset, type VisibilityState } from "../rules/visibility.js";
import { ROWS_PER_PAGE, browseCrumb, browseInto, browsePage, browseRows, browseUp, canGoUp, initialBrowse, pageCount, type BrowseState } from "../rules/browse-nav.js";

/** The honesty-ladder card text for the current selection; see `rules/info-resolver.ts`. */
export interface CardInfo {
  text: string;
  source: string;
}

/** On-device diagnostics; painted into the Status window from the 500 ms refresh cadence. */
export interface StatusReport {
  bundle: string;
  level: string;
  mode: "2D" | "VR" | "AR";
  blendMode: string;
  multiview: boolean;
  /** Whether `"layers"` was granted at session start; `null` with no live session. */
  layers: boolean | null;
  /** The one-time `isSessionSupported("immersive-ar")` probe; `null` while it is still running. */
  arSupported: boolean | null;
  hitTest: "n/a" | "available" | "unavailable";
  fps: number;
  drawCalls: number;
  triangles: number;
  /** The last perf sample as one line (`perfLine` in perf-monitor.ts). */
  perfLine: string;
  placementPhase: string;
  consoleErrors: number;
  logSessionId: string;
  logStreaming: boolean;
}

/** Attribution, versions and the controls summary; static once the bundle has loaded. */
export interface AboutReport {
  bundleId: string;
  run: string;
  threeRevision: string;
  iwsdkCore: string;
  multiviewFix: boolean;
  controlsSummary: string;
}

/** Everything the windows need from the app, and nothing more. */
export interface AtlasUiPort {
  catalog(): Catalog;
  /** Layer state of the body the controls act on. */
  visibility(): VisibilityState;
  selected(): Group | null;
  comparing(): boolean;
  status(): string;
  /** The bodies the controls can be aimed at; empty while there is only one. */
  bodyOptions(): { id: string | null; label: string }[];
  /** The body the controls act on, or `null` for all of them. */
  activeBody(): string | null;
  setActiveBody(id: string | null): void;

  applyPreset(preset: LayerPreset): void;
  toggleSystem(systemId: string): void;
  selectGroup(group: Group): void;
  hideSelected(): void;
  showEverything(): void;
  nudgeScale(factor: number): void;
  nudgeYaw(radians: number): void;
  setComparing(on: boolean): void;
  reset(): void;

  /** The honesty-ladder text for the current selection, or `null` with nothing to show. */
  cardInfo(): CardInfo | null;
  /** The one-time AR probe's result; `null` while it is still running. */
  arSupported(): boolean | null;
  /** Whether the live session is AR (by blend mode), for the "Place"/"Back" label. */
  inAr(): boolean;
  /** Enter AR from 2D/VR, or return to the VR studio from AR. */
  toggleAr(): void;

  diagnostics(): StatusReport;
  about(): AboutReport;
}

/** Slots in systems.uikitml. Must be at least the systems in any bundle opened. */
const SYSTEM_SLOTS = 16;

/**
 * Slots in info.uikitml's ACTING ON row. Must be at least the comparison's body
 * count plus one, for the "All three" entry.
 */
const TARGET_SLOTS = 4;

const ON = "rgba(58, 88, 118, 0.95)";
const OFF = "rgba(30, 42, 55, 0.9)";

interface Bound {
  panel: PanelHandle;
  paint(): void;
}

export class AtlasWindows {
  private readonly bound: Bound[] = [];
  /** Binders waiting for their panel, keyed by the window id given to the factory. */
  private readonly pending = new Map<string, (panel: PanelHandle) => () => void>();
  private host: IwsdkSceneHost | null = null;
  private browse: BrowseState = initialBrowse();
  /** Every window's entity, so the set can be shown in a session and hidden on the desk. */
  private readonly entities: Entity[] = [];
  private visible = true;

  constructor(
    private readonly world: World,
    private readonly port: AtlasUiPort,
  ) {}

  /**
   * Spawn the three windows, arranged around the person: Browse to the left,
   * Systems to the right, the card low and centred where a glance down finds
   * it without covering the body.
   */
  create(): void {
    // One host per world, subscribed before anything is spawned. The host
    // announces each panel as IWSDK attaches its document, keyed by the id the
    // factory was given. Late subscribers are replayed, so the order is a
    // courtesy rather than a need.
    if (!this.host) {
      this.host = createSceneHost(this.world);
      this.host.onPanelReady(({ id, panel }) => this.attach(id, panel));
    }
    // Browse and Systems flank the body at eye height; the card sits below the
    // eye line so the torso and head stay clear. All three are movable, because
    // no default suits every height and every room.
    this.spawn("atlas-browse", "Browse", "ui/browse.uikitml", [-0.7, 1.32, -0.82], (p) => this.bindBrowse(p), 0.62);
    this.spawn("atlas-systems", "Systems", "ui/systems.uikitml", [0.7, 1.32, -0.82], (p) => this.bindSystems(p), 0.62);
    this.spawn("atlas-info", "Selected", "ui/info.uikitml", [0, 0.66, -0.95], (p) => this.bindInfo(p), 0.46);
    // Status and About sit lower, flanking Selected: diagnostics for the first
    // headset session and attribution, neither needed as often as the three above.
    this.spawn("atlas-status", "Status", "ui/status.uikitml", [-0.7, 0.36, -0.82], (p) => this.bindStatus(p), 0.4);
    this.spawn("atlas-about", "About", "ui/about.uikitml", [0.7, 0.36, -0.82], (p) => this.bindAbout(p), 0.42);
  }

  /** How many windows have parsed and bound; the smoke test asserts this. */
  boundCount(): number {
    return this.bound.length;
  }

  /**
   * Show or hide every window. The windows are the headset's interface: on a
   * desk the DOM panel carries the same controls, so the app hides them until
   * a session starts and again when it ends. Hiding the entity's object hides
   * the panel and its chrome together; pointer events are switched off with
   * it so an invisible window cannot still take a ray.
   */
  setVisible(on: boolean): void {
    this.visible = on;
    for (const entity of this.entities) this.applyVisibility(entity);
  }

  isVisible(): boolean {
    return this.visible;
  }

  private applyVisibility(entity: Entity): void {
    const object = entity.object3D;
    if (!object) return;
    object.visible = this.visible;
    object.pointerEvents = this.visible ? "auto" : "none";
  }

  /** Repaint every window. Cheap: it writes text and colours, it does not rebuild. */
  paint(): void {
    for (const b of this.bound) b.paint();
  }

  private spawn(id: string, title: string, config: string, position: [number, number, number], bind: (panel: PanelHandle) => () => void, maxHeight: number): void {
    // The document lands over later frames and the host announces it through
    // onPanelReady, keyed by this id. The id is the contract: a window spawned
    // without one is announced as kind "panel" under its config path instead,
    // and would never match here. There is no poll and no deadline; a window
    // that never parses never binds, and the smoke test reports it as missing.
    this.pending.set(id, bind);
    const entity = createUIWindow(this.world, { id, title, config, position, movable: true, minimizable: true, pinnable: true, maxWidth: 0.5, maxHeight });
    this.entities.push(entity);
    this.applyVisibility(entity);
  }

  /** Bind a window whose panel has just arrived; panels that are not ours are left alone. */
  private attach(id: string, panel: PanelHandle): void {
    const bind = this.pending.get(id);
    if (!bind) return;
    this.pending.delete(id);
    const paint = bind(panel);
    this.bound.push({ panel, paint });
    paint();
  }

  // --- Systems -------------------------------------------------------------

  private bindSystems(panel: PanelHandle): () => void {
    const presets: (UixElement | undefined)[] = [];
    for (let i = 0; i < LAYER_PRESETS.length; i++) {
      const el = panel.getElementById(`preset-${i}`);
      const preset = LAYER_PRESETS[i]!;
      el?.addEventListener("click", () => {
        this.port.applyPreset(preset);
        this.paint();
      });
      presets.push(el);
    }

    const rows: { row?: UixElement; chip?: UixElement; name?: UixElement; state?: UixElement }[] = [];
    for (let i = 0; i < SYSTEM_SLOTS; i++) {
      const row = panel.getElementById(`sys-${i}`);
      row?.addEventListener("click", () => {
        const system = this.port.catalog().systems[i];
        if (!system) return;
        this.port.toggleSystem(system.id);
        this.paint();
      });
      rows.push({ row, chip: panel.getElementById(`sys-chip-${i}`), name: panel.getElementById(`sys-name-${i}`), state: panel.getElementById(`sys-state-${i}`) });
    }

    return () => {
      for (let i = 0; i < LAYER_PRESETS.length; i++) presets[i]?.setProperties({ text: LAYER_PRESETS[i]!.name });
      const catalog = this.port.catalog();
      const visible = this.port.visibility().systems;
      for (let i = 0; i < SYSTEM_SLOTS; i++) {
        const slot = rows[i]!;
        const system = catalog.systems[i];
        if (!system) {
          slot.row?.setProperties({ display: "none" });
          continue;
        }
        const on = visible.has(system.id);
        slot.row?.setProperties({ display: "flex", backgroundColor: on ? ON : OFF });
        slot.chip?.setProperties({ backgroundColor: system.colour });
        slot.name?.setProperties({ text: `${system.name} (${system.parts.length})` });
        slot.state?.setProperties({ text: on ? "ON" : "off", color: on ? "#86e08a" : "#6d8296" });
      }
    };
  }

  // --- Browse --------------------------------------------------------------

  private bindBrowse(panel: PanelHandle): () => void {
    const crumb = panel.getElementById("crumb");
    const crumbUp = panel.getElementById("crumb-up");
    panel.getElementById("crumb-bar")?.addEventListener("click", () => {
      this.browse = browseUp(this.browse);
      this.paint();
    });

    const rows: { row?: UixElement; name?: UixElement; count?: UixElement }[] = [];
    for (let i = 0; i < ROWS_PER_PAGE; i++) {
      const row = panel.getElementById(`row-${i}`);
      row?.addEventListener("click", () => {
        const visible = browseRows(this.port.catalog(), this.browse);
        const hit = visible[i];
        if (!hit) return;
        // select first, so a drill still leaves the thing you pressed showing
        this.port.selectGroup(hit.group);
        this.browse = browseInto(this.port.catalog(), this.browse, hit.id);
        this.paint();
      });
      rows.push({ row, name: panel.getElementById(`row-name-${i}`), count: panel.getElementById(`row-count-${i}`) });
    }

    const label = panel.getElementById("page-label");
    panel.getElementById("page-prev")?.addEventListener("click", () => {
      this.browse = browsePage(this.port.catalog(), this.browse, -1);
      this.paint();
    });
    panel.getElementById("page-next")?.addEventListener("click", () => {
      this.browse = browsePage(this.port.catalog(), this.browse, 1);
      this.paint();
    });

    return () => {
      const catalog = this.port.catalog();
      crumb?.setProperties({ text: browseCrumb(catalog, this.browse) });
      crumbUp?.setProperties({ text: canGoUp(this.browse) ? "▲ BACK" : "" });
      const visible = browseRows(catalog, this.browse);
      const selected = this.port.selected();
      for (let i = 0; i < ROWS_PER_PAGE; i++) {
        const slot = rows[i]!;
        const hit = visible[i];
        if (!hit) {
          slot.row?.setProperties({ display: "none" });
          continue;
        }
        const isSelected = selected?.id === hit.group.id;
        slot.row?.setProperties({ display: "flex", backgroundColor: isSelected ? ON : OFF });
        slot.name?.setProperties({ text: hit.name });
        slot.count?.setProperties({ text: hit.drills ? `${hit.count} ›` : `${hit.count}` });
      }
      const pages = pageCount(catalog, this.browse);
      label?.setProperties({ text: pages > 1 ? `${this.browse.page + 1} / ${pages}` : "" });
    };
  }

  // --- Info card and body controls -----------------------------------------

  private bindInfo(panel: PanelHandle): () => void {
    const kind = panel.getElementById("card-kind");
    const name = panel.getElementById("card-name");
    const meta = panel.getElementById("card-meta");
    const source = panel.getElementById("card-source");
    const text = panel.getElementById("card-text");
    const cite = panel.getElementById("card-cite");
    const status = panel.getElementById("status-line");
    const compare = panel.getElementById("act-compare");
    const arButton = panel.getElementById("act-ar");

    // Which body the controls act on. The same choice the desk panel offers and
    // the same one a press on a body makes, so the three routes agree.
    const targetSection = panel.getElementById("target-section");
    const targetSlots: (UixElement | undefined)[] = [];
    for (let i = 0; i < TARGET_SLOTS; i++) {
      const el = panel.getElementById(`target-${i}`);
      el?.addEventListener("click", () => {
        const option = this.port.bodyOptions()[i];
        if (!option) return;
        this.port.setActiveBody(option.id);
        this.paint();
      });
      targetSlots.push(el);
    }

    panel.getElementById("act-isolate")?.addEventListener("click", () => {
      const group = this.port.selected();
      if (group) this.port.selectGroup(group);
      this.paint();
    });
    panel.getElementById("act-hide")?.addEventListener("click", () => {
      this.port.hideSelected();
      this.paint();
    });
    panel.getElementById("act-showall")?.addEventListener("click", () => {
      this.port.showEverything();
      this.paint();
    });
    panel.getElementById("act-bigger")?.addEventListener("click", () => this.port.nudgeScale(1.25));
    panel.getElementById("act-smaller")?.addEventListener("click", () => this.port.nudgeScale(0.8));
    panel.getElementById("act-turn-left")?.addEventListener("click", () => this.port.nudgeYaw(Math.PI / 8));
    panel.getElementById("act-turn-right")?.addEventListener("click", () => this.port.nudgeYaw(-Math.PI / 8));
    panel.getElementById("act-compare")?.addEventListener("click", () => {
      this.port.setComparing(!this.port.comparing());
      this.paint();
    });
    panel.getElementById("act-reset")?.addEventListener("click", () => {
      this.port.reset();
      this.paint();
    });
    arButton?.addEventListener("click", () => {
      this.port.toggleAr();
      this.paint();
    });

    return () => {
      // the selector only exists while there is more than one body to aim at
      const options = this.port.bodyOptions();
      const active = this.port.activeBody();
      targetSection?.setProperties({ display: options.length ? "flex" : "none" });
      for (let i = 0; i < TARGET_SLOTS; i++) {
        const option = options[i];
        if (!option) {
          targetSlots[i]?.setProperties({ display: "none" });
          continue;
        }
        targetSlots[i]?.setProperties({ display: "flex", text: option.label, backgroundColor: active === option.id ? ON : OFF });
      }

      const group = this.port.selected();
      const catalog = this.port.catalog();
      if (group) {
        const first = catalog.byId.get(group.parts[0] ?? "");
        const system = first ? catalog.systemById.get(first.system) : undefined;
        const tris = group.parts.reduce((total, id) => total + (catalog.byId.get(id)?.tris ?? 0), 0);
        kind?.setProperties({ text: group.kind.toUpperCase() });
        name?.setProperties({ text: group.name });
        meta?.setProperties({ text: `${group.parts.length} part${group.parts.length === 1 ? "" : "s"} - ${tris.toLocaleString()} triangles${system ? ` - ${system.name}` : ""}` });
        // FMA ids are the source's own identity: worth showing, per the licence
        source?.setProperties({ text: group.kind === "concept" ? `FMA ${group.id.replace(/^FMA/, "")}` : group.id });
      } else {
        kind?.setProperties({ text: "" });
        name?.setProperties({ text: "Nothing selected" });
        meta?.setProperties({ text: "Pick a system or a structure from Browse." });
        source?.setProperties({ text: "" });
      }
      const info = this.port.cardInfo();
      text?.setProperties({ text: info?.text ?? "" });
      cite?.setProperties({ text: info?.source ?? "" });
      compare?.setProperties({ text: this.port.comparing() ? "Single body" : "Compare three" });
      const arSupported = this.port.arSupported();
      arButton?.setProperties({ display: arSupported === false ? "none" : "flex", text: this.port.inAr() ? "Back to the studio" : "Place in my room" });
      status?.setProperties({ text: this.port.status() });
    };
  }

  // --- Status: on-device diagnostics ---------------------------------------

  private bindStatus(panel: PanelHandle): () => void {
    const bundle = panel.getElementById("status-bundle");
    const mode = panel.getElementById("status-mode");
    const blend = panel.getElementById("status-blend");
    const features = panel.getElementById("status-features");
    const ar = panel.getElementById("status-ar");
    const hitTest = panel.getElementById("status-hittest");
    const perf = panel.getElementById("status-perf");
    const phase = panel.getElementById("status-phase");
    const errors = panel.getElementById("status-errors");
    const log = panel.getElementById("status-log");

    return () => {
      const d = this.port.diagnostics();
      bundle?.setProperties({ text: `${d.bundle} - ${d.level}` });
      mode?.setProperties({ text: `Session: ${d.mode}` });
      blend?.setProperties({ text: `Blend mode: ${d.blendMode}` });
      features?.setProperties({ text: `Multiview: ${d.multiview ? "yes" : "no"} - Layers: ${d.layers === null ? "n/a" : d.layers ? "yes" : "no"}` });
      ar?.setProperties({ text: `AR supported: ${d.arSupported === null ? "checking…" : d.arSupported ? "yes" : "no"}` });
      hitTest?.setProperties({ text: `Hit-test: ${d.hitTest}` });
      perf?.setProperties({ text: d.perfLine });
      phase?.setProperties({ text: `Placement: ${d.placementPhase}` });
      errors?.setProperties({ text: `Console errors: ${d.consoleErrors}` });
      log?.setProperties({ text: `Log session: ${d.logSessionId}${d.logStreaming ? "" : " (not streaming — add ?remote-log)"}` });
    };
  }

  // --- About: attribution, disclaimer, versions, controls -------------------

  private bindAbout(panel: PanelHandle): () => void {
    const attrib1 = panel.getElementById("about-attribution-1");
    const attrib2 = panel.getElementById("about-attribution-2");
    const disclaimer = panel.getElementById("about-disclaimer");
    const bundle = panel.getElementById("about-bundle");
    const three = panel.getElementById("about-three");
    const iwsdk = panel.getElementById("about-iwsdk");
    const multiview = panel.getElementById("about-multiview");
    const controls = panel.getElementById("about-controls");

    attrib1?.setProperties({ text: "Anatomy from BodyParts3D, © The Database Center for Life Science, CC BY 4.0." });
    attrib2?.setProperties({ text: "Descriptions from Human Atlas (MIT)." });
    disclaimer?.setProperties({ text: "Educational reference only — not medical advice or a diagnostic tool." });

    return () => {
      const a = this.port.about();
      bundle?.setProperties({ text: `Bundle: ${a.bundleId} - run ${a.run}` });
      three?.setProperties({ text: `three r${a.threeRevision}` });
      iwsdk?.setProperties({ text: `@iwsdk/core ${a.iwsdkCore}` });
      multiview?.setProperties({ text: `Multiview fix: ${a.multiviewFix ? "applied" : "not applied"}` });
      controls?.setProperties({ text: a.controlsSummary });
    };
  }
}
